import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  Media,
  MediaPickerComponent,
  MediaStatus,
  MediaType,
  Product,
  WorkspaceAsset,
} from '@gripp/shared-logic';
import { useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, View, ViewStyle } from 'react-native';
import { useOpenedState } from '../hooks';
import { Colors } from '../themes/colors';
import LoadingIndicator from './loadingIndicator';
import { ImageData, SizePrefix, useTypedMediaItem } from './media';
import { ImageLightBox } from './modalImages/imageLightbox';

import type { ComponentProps } from 'react';
import { ChangeAssetImage } from './asset/changeAssetImage';
import { ImageComponent } from './crop-circle/Components/Image';
import { ChangeProductImage } from './product/changeProductImage';
import { themedStyleSheet } from '../themes/themedStyleSheet';

const TIMEOUT_MS = 60000; // 1 minute

export type HeaderCoverImageProps = {
  media?: Media;
  imgSize?: number;
  maxRetries?: number;
  retryDelay?: number;
  timeoutMs?: number;
  invalidateQuery?: () => void;
  iconName?: ComponentProps<typeof MaterialCommunityIcons>['name'];
  style?: ViewStyle;
  workspaceAsset?: WorkspaceAsset;
  product?: Product;
  mediaPicker?: MediaPickerComponent;
  onImageChanged?: () => void;
  onImagePress?: () => void;
};

export const HeaderCoverImage = (props: HeaderCoverImageProps) => {
  const { isOpened, onOpen, onClose } = useOpenedState();

  const [retryCount, setRetryCount] = useState<number>(0);
  const maxRetries = props.maxRetries ?? 5;
  const retryDelay = props.retryDelay ?? 5000;
  const timeoutMs = props.timeoutMs ?? TIMEOUT_MS;
  const { typedMediaItem: coverImage, handleRefreshMediaItem } =
    useTypedMediaItem<ImageData>(props.media);

  // Check if media has timed out based on updatedAt
  const hasTimedOut = useMemo(() => {
    if (props?.media?.status !== MediaStatus.Pending) return false;
    const updatedAt = (props.media as any)?.updatedAt;
    if (!updatedAt) return false;
    const elapsed = Date.now() - new Date(updatedAt).getTime();
    return elapsed > timeoutMs;
  }, [props?.media?.status, (props?.media as any)?.updatedAt, timeoutMs]);

  const isLoading = useMemo(
    () =>
      props?.media?.status === MediaStatus.Pending &&
      retryCount < maxRetries &&
      !hasTimedOut &&
      !props.media?.localUrl,
    [
      props?.media?.status,
      props?.media?.localUrl,
      retryCount,
      maxRetries,
      hasTimedOut,
    ]
  );

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (!props?.media) {
      handleRefreshMediaItem(undefined);
      return;
    }

    if (isLoading) {
      timeoutId = setTimeout(() => {
        props.invalidateQuery?.();
        setRetryCount((prev) => prev + 1);
      }, retryDelay);
    } else {
      setRetryCount(0);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    props?.media?.status,
    retryCount,
    maxRetries,
    hasTimedOut,
    retryDelay,
    handleRefreshMediaItem,
  ]);

  const handleRetry = () => {
    setRetryCount(0);
    props.invalidateQuery?.();
  };

  const handleSaveImage = () => {
    handleRefreshMediaItem(undefined);
    props?.invalidateQuery?.();
    onClose();
  };

  // Get appropriate icon for different media types
  const getMediaTypeIcon = (
    mediaType?: MediaType
  ): ComponentProps<typeof MaterialCommunityIcons>['name'] => {
    switch (mediaType) {
      case MediaType.Image:
        return 'image';
      case MediaType.Document:
        return 'file-document-outline';
      case MediaType.Video:
        return 'video-outline';
      case MediaType.Audio:
        return 'music-note';
      default:
        return props.iconName || 'qrcode';
    }
  };

  /**
   * Renders a placeholder image if no cover image is available.
   * If a media picker and workspace asset are provided, it allows changing the image.
   * If either are not available, a default icon is shown.
   */
  const renderPlaceholderImage = () => {
    if (props.workspaceAsset && props.mediaPicker) {
      return (
        <ChangeAssetImage
          workspaceAsset={props.workspaceAsset!}
          mediaPicker={props.mediaPicker!}
          onClose={handleSaveImage}
          customTrigger={<ImageComponent showClose={false} />}
        />
      );
    }

    if (props.product && props.mediaPicker) {
      return (
        <ChangeProductImage
          product={props.product!}
          mediaPicker={props.mediaPicker!}
          onClose={handleSaveImage}
          customTrigger={
            <MaterialCommunityIcons
              name={getMediaTypeIcon(props.media?.type)}
              size={props.imgSize || 56}
              style={[styles.cardImage, props.style]}
            />
          }
        />
      );
    }

    return (
      <MaterialCommunityIcons
        name={getMediaTypeIcon(props.media?.type)}
        size={props.imgSize || 56}
        style={[styles.cardImage, props.style]}
      />
    );
  };

  const onButtonChange = useMemo(() => {
    if (props.workspaceAsset && props.mediaPicker) {
      return (
        <ChangeAssetImage
          workspaceAsset={props.workspaceAsset}
          mediaPicker={props.mediaPicker}
          onClose={handleSaveImage}
        />
      );
    }

    if (props.product && props.mediaPicker) {
      return (
        <ChangeProductImage
          product={props.product}
          mediaPicker={props.mediaPicker}
          onClose={handleSaveImage}
          onImageChanged={props.onImageChanged}
        />
      );
    }

    return undefined;
  }, [props.workspaceAsset, props.product, props.mediaPicker, handleSaveImage]);

  return (
    <View>
      {coverImage ? (
        <>
          <Pressable onPress={props.onImagePress ?? onOpen}>
            {isLoading ? (
              <View
                style={[
                  styles.cardImage,
                  {
                    width: props.imgSize || 88,
                    height: props.imgSize || 88,
                    borderWidth: 0,
                  },
                ]}
              >
                <LoadingIndicator
                  size={20}
                  backgroundColor={Colors.role.surface}
                />
              </View>
            ) : hasTimedOut ? (
              <Pressable onPress={handleRetry}>
                <View
                  style={[
                    styles.cardImage,
                    {
                      width: props.imgSize || 88,
                      height: props.imgSize || 88,
                      borderWidth: 0,
                    },
                  ]}
                >
                  <MaterialCommunityIcons
                    name="alert-circle-outline"
                    color={Colors.red}
                    size={20}
                  />
                </View>
              </Pressable>
            ) : props.media?.type === MediaType.Image ? (
              <ImageComponent
                source={{
                  uri:
                    props.media?.localUrl ||
                    coverImage?.urlsBySize?.[SizePrefix.extraSmall],
                }}
                showClose={false}
              />
            ) : (
              <ImageComponent showClose={false} />
            )}
          </Pressable>
          <ImageLightBox
            url={coverImage.urlsBySize?.original || coverImage.url}
            isOpened={isOpened}
            onClose={onClose}
            onButtonChange={onButtonChange}
          />
        </>
      ) : (
        renderPlaceholderImage()
      )}
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  cardImage: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    ...Platform.select({
      web: {
        marginRight: 20,
        borderWidth: 1,
        borderColor: role.borderStrong,
      },
      default: {
        marginRight: 16,
      },
    }),
  },
}));
